import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Image, Link2, Pencil, Plus, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';
// Red Team F#15: shadcn Alert primitive does not exist in components/ui/.
// Use the existing Card with a yellow border for the missing-coverage banner.
import { Card } from '@/components/ui/card';
import { Game, UpdateGameInput, CreateGameInput } from './games-types';
import { GameForm } from './games-form';
import { useCreateGame, useDeleteGame, useGames, useUpdateGame } from './games-queries';
import { PLATFORMS, Platform } from '@/features/version-mappings/version-mappings-types';
import { useVersionMappings } from '@/features/version-mappings/version-mappings-queries';
import { formatDate } from '@/lib/format';

export function GamesPage(): JSX.Element {
  const { data: games, isLoading, error } = useGames();
  // Red Team F#4: missing-coverage banner uses ONE single-list call (no
  // filters), grouped client-side by (game_id, platform). This avoids
  // N+1 round-trips on the games list.
  const { data: allMappings } = useVersionMappings();
  const coverageByGame = useMemo(() => {
    const map = new Map<string, Set<Platform>>();
    for (const m of allMappings ?? []) {
      let set = map.get(m.game_id);
      if (!set) {
        set = new Set<Platform>();
        map.set(m.game_id, set);
      }
      set.add(m.platform);
    }
    return map;
  }, [allMappings]);
  const createMutation = useCreateGame();
  const updateMutation = useUpdateGame();
  const deleteMutation = useDeleteGame();

  const [createOpen, setCreateOpen] = useState(false);
  const [editing, setEditing] = useState<Game | null>(null);
  const [deleting, setDeleting] = useState<Game | null>(null);

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-semibold tracking-tight">Games</h2>
          <p className="text-sm text-muted-foreground">Manage games and their version mappings.</p>
        </div>
        <Button onClick={() => setCreateOpen(true)}>
          <Plus className="mr-2 h-4 w-4" /> New Game
        </Button>
      </div>

      {isLoading && <p className="text-muted-foreground">Loading…</p>}
      {error && <p className="text-destructive">Failed to load games.</p>}

      {games && games.length === 0 && (
        <div className="rounded-lg border border-dashed p-8 text-center">
          <p className="text-muted-foreground">No games yet. Click "New Game" to create one.</p>
        </div>
      )}

      {games && games.length > 0 && (
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Package name</TableHead>
              <TableHead>Name</TableHead>
              <TableHead>Platform coverage</TableHead>
              <TableHead>Created</TableHead>
              <TableHead className="w-24">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {games.map((g) => {
              const covered = coverageByGame.get(g.id) ?? new Set<Platform>();
              const missing = PLATFORMS.filter((p) => !covered.has(p));
              return (
                <TableRow key={g.id}>
                  <TableCell className="font-mono text-sm">{g.package_name}</TableCell>
                  <TableCell>{g.name}</TableCell>
                  <TableCell>
                    <div className="flex flex-col gap-1">
                      <div className="flex gap-1 flex-wrap">
                        {PLATFORMS.map((p) => (
                          <span
                            key={p}
                            className={
                              covered.has(p)
                                ? 'rounded bg-green-100 px-2 py-0.5 text-xs font-mono text-green-900'
                                : 'rounded bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground line-through'
                            }
                          >
                            {p}
                          </span>
                        ))}
                      </div>
                      {missing.length > 0 && (
                        <Card className="border-yellow-500 bg-yellow-50 p-2 text-xs text-yellow-900">
                          Missing platform coverage: {missing.join(', ')}. Clients on those
                          platforms will get 404 until mappings are added.
                        </Card>
                      )}
                    </div>
                  </TableCell>
                  <TableCell className="text-muted-foreground text-sm">
                    {formatDate(g.created_at)}
                  </TableCell>
                  <TableCell>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        asChild
                        aria-label="Edit Catalog"
                        title="Edit catalog (description, icon, store links, gallery)"
                      >
                        <Link to={`/games/${g.id}/catalog`}>
                          <Image className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        asChild
                        aria-label="Manage Base URLs"
                      >
                        <Link to={`/games/${g.id}/base-urls`}>
                          <Link2 className="h-4 w-4" />
                        </Link>
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setEditing(g)}
                        aria-label="Edit"
                      >
                        <Pencil className="h-4 w-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={() => setDeleting(g)}
                        aria-label="Delete"
                      >
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      )}

      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>New Game</DialogTitle>
            <DialogDescription>Create a game. You'll be able to add version mappings next.</DialogDescription>
          </DialogHeader>
          <GameForm
            onSubmit={async (data) => {
              await createMutation.mutateAsync(data as CreateGameInput);
              setCreateOpen(false);
            }}
            onCancel={() => setCreateOpen(false)}
            isSubmitting={createMutation.isPending}
          />
        </DialogContent>
      </Dialog>

      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Game</DialogTitle>
            <DialogDescription>package_name is locked. Only the display name can be changed.</DialogDescription>
          </DialogHeader>
          {editing && (
            <GameForm
              initial={editing}
              onSubmit={async (data) => {
                await updateMutation.mutateAsync({ id: editing.id, input: data as UpdateGameInput });
                setEditing(null);
              }}
              onCancel={() => setEditing(null)}
              isSubmitting={updateMutation.isPending}
            />
          )}
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!deleting} onOpenChange={(o) => !o && setDeleting(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete game?</AlertDialogTitle>
            <AlertDialogDescription>
              Permanently delete <code>{deleting?.package_name}</code>? Version mappings and
              maintenance windows for this game will block the delete (foreign key constraint).
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={async () => {
                if (deleting) {
                  await deleteMutation.mutateAsync(deleting.id);
                  setDeleting(null);
                }
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
